"use client";
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useContext, useId, useLayoutEffect, useMemo, useReducer, useRef } from "react";
import { math } from "polished";
import ChevronDownIcon from "../../icons/chevron-down.js";
import ChevronUpIcon from "../../icons/chevron-up.js";
import renderIcon from "../../icons/render-icon.js";
import makeStyles from "../../theming/make-styles.js";
import Attachment from "../attachment/index.js";
import { contains } from "../dropdown-base/dropdown-base.js";
import isElement from "../utils/is-element.js";
import { useScrollIntoView } from "../utils/scroll-into-view.js";
import useCombinedRefs from "../utils/use-combined-refs.js";
import { MenuContext } from "./menu.js";
function getItemsAlignment(alignment) {
    switch (alignment) {
        case "start": {
            return "flex-start";
        }
        case "end": {
            return "flex-end";
        }
        default: {
            return "center";
        }
    }
}
const useStyles = makeStyles()((theme, { alignItems, clickable, color, indentLevel }, classes) => {
    const palette = theme.palette(color).menu;
    return {
        active: {
            color: palette.item.active.color,
            backgroundColor: palette.item.active.backgroundColor,
            fontWeight: theme.typography.fontWeights.bold,
            [`.${classes.nested} &`]: {
                [`& .${classes.leftIcon}`]: {
                    color: palette.item.icon.color,
                },
            },
        },
        attachment: {
            backgroundColor: palette.attachment.backgroundColor,
            border: `${theme.size(1)} solid ${palette.attachment.borderColor}`,
            borderRadius: theme.size(4),
            boxShadow: theme.shadow("sm"),
            outline: 0,
            [`& .${classes.label}`]: Object.assign({ display: "block", padding: theme.spacing("sm", "md"), marginBottom: 0, borderBottom: `${theme.size(1)} solid ${palette.attachment.label.borderColor}` }, theme.typography.body),
        },
        focus: {},
        item: {},
        label: {},
        leftIcon: {
            flexShrink: 0,
        },
        nested: {},
        rightIcon: {
            flexShrink: 0,
        },
        text: {
            flexGrow: 1,
            minWidth: 0,
            overflowWrap: "break-word",
            [`.${classes.leftIcon} + &`]: {
                marginLeft: theme.spacing(12),
            },
            [`& + .${classes.rightIcon}`]: {
                marginLeft: theme.spacing(12),
            },
        },
        trigger: {
            position: "absolute",
            top: 0,
            right: -1,
            width: theme.size(1),
            height: theme.size(1),
        },
        wrapper: {
            "position": "relative",
            "display": "flex",
            "alignItems": getItemsAlignment(alignItems),
            "minHeight": math(`(${theme.typography.body.lineHeight} * ${theme.typography.body.fontSize}) + (2 * ${theme.spacing("sm")})`),
            "color": "inherit",
            "cursor": clickable ? "pointer" : undefined,
            "&:not(:empty)": {
                padding: theme.spacing("sm", "md"),
                paddingLeft: math(`${indentLevel} * ${theme.spacing("xl")} - ${theme.spacing("md")}`),
            },
            "&:focus": {
                outline: 0,
            },
            "&:hover": {
                backgroundColor: clickable ? palette.item.link.hover.backgroundColor : undefined,
                [`.${classes.nested} > &`]: {
                    backgroundColor: palette.item.link.hover.backgroundColor,
                },
                [`.${classes.active} &`]: {
                    backgroundColor: clickable ? palette.item.link.active.hover.backgroundColor : undefined,
                },
                [`.${classes.active}.${classes.nested} > &`]: {
                    backgroundColor: palette.item.link.active.hover.backgroundColor,
                },
                [`.${classes.active}.${classes.focus}.${classes.nested} > &`]: {
                    backgroundColor: palette.item.link.active.hover.backgroundColor,
                },
            },
            [`.${classes.active}.${classes.nested} .${classes.active} &`]: {
                fontWeight: theme.typography.fontWeights.bold,
            },
            [`.${classes.focus}.${classes.nested} > &`]: {
                backgroundColor: palette.item.link.hover.backgroundColor,
            },
            [`.${classes.active}.${classes.focus}.${classes.nested} > &`]: {
                backgroundColor: palette.item.active.backgroundColor,
            },
            [`.${classes.nested} > &`]: {
                cursor: "pointer",
            },
            [`.${classes.nested} .${classes.item} &`]: Object.assign({}, theme.typography.small),
            [theme.breakpoints.down("sm")]: {
                padding: theme.spacing("sm", "xl"),
            },
        },
    };
});
export const defaultToggleIcon = (open) => (open ? ChevronUpIcon : ChevronDownIcon);
function useOpen(collapsed, enable, initial = false) {
    const [expandedOpen, toggleExpandedOpen] = useReducer((state, action) => (typeof action === "boolean" ? action : !state), initial);
    const [collapsedOpen, setCollapsedOpen] = useContext(MenuContext).openState;
    const id = useId();
    const open = collapsed ? collapsedOpen === id : expandedOpen;
    const toggleCollapsedOpen = useCallback((toggle) => setCollapsedOpen(copen => ((typeof toggle === "boolean" ? toggle : copen !== id) ? id : undefined)), [setCollapsedOpen, id]);
    useLayoutEffect(() => {
        if (collapsed) {
            toggleCollapsedOpen(false);
        }
        toggleExpandedOpen(initial);
    }, [collapsed, initial, toggleCollapsedOpen, toggleExpandedOpen]);
    const toggleOpen = useMemo(() => (enable ? (collapsed ? toggleCollapsedOpen : toggleExpandedOpen) : undefined), [collapsed, enable, toggleCollapsedOpen]);
    return [open, toggleOpen];
}
export const MenuItem = React.forwardRef(function MenuItem(_a, ref) {
    var { active = false, alignItems, children, className, collapsed, indentLevel = 1, label, leftIcon, rightIcon, scrollToView, toggleIcon = defaultToggleIcon, onClick } = _a, props = __rest(_a, ["active", "alignItems", "children", "className", "collapsed", "indentLevel", "label", "leftIcon", "rightIcon", "scrollToView", "toggleIcon", "onClick"]);
    const _b = useContext(MenuContext), { onMenuClose } = _b, context = __rest(_b, ["onMenuClose"]);
    const clickable = (isElement(label) && (label.props.href !== undefined || !!label.props.onClick)) || !!onClick;
    const { classes, cx } = useStyles({
        alignItems: alignItems || context.alignItems,
        clickable,
        color: context.color,
        indentLevel,
    });
    const attachmentRef = useRef(null);
    const innerRef = useRef(null);
    const combinedRef = useCombinedRefs(ref, innerRef);
    const menuItemRef = useRef(null);
    const submenu = children ? React.Children.only(children) : undefined;
    const nestedActive = useMemo(() => { var _a; return (_a = React.Children.map(submenu === null || submenu === void 0 ? void 0 : submenu.props.children, p => isElement(p) && p.props.active)) === null || _a === void 0 ? void 0 : _a.some(Boolean); }, [submenu === null || submenu === void 0 ? void 0 : submenu.props.children]);
    const nestedOpen = submenu === null || submenu === void 0 ? void 0 : submenu.props.open;
    const [open, toggleOpen] = useOpen(collapsed, !!children && typeof nestedOpen !== "boolean", !!active || nestedActive);
    const openIcon = !children || typeof nestedOpen === "boolean" ? undefined : toggleIcon(open);
    useScrollIntoView(scrollToView, menuItemRef);
    const handleBlur = useCallback((e) => {
        if (contains(e.relatedTarget instanceof Element ? e.relatedTarget : null, innerRef.current, attachmentRef.current)) {
            return;
        }
        toggleOpen === null || toggleOpen === void 0 ? void 0 : toggleOpen();
    }, [toggleOpen]);
    const handleClick = useCallback((e) => {
        if (isElement(label) && "onClick" in label.props) {
            label.props.onClick(e);
        }
        onMenuClose === null || onMenuClose === void 0 ? void 0 : onMenuClose();
    }, [label, onMenuClose]);
    return (_jsxs("li", Object.assign({ ref: menuItemRef, className: cx(classes.item, {
            [classes.active]: active,
            [classes.focus]: collapsed && open && !!submenu,
            [classes.nested]: !!children && (typeof nestedOpen !== "boolean" || nestedOpen),
        }, className), "data-component": "menu-item", role: "treeitem", onClick: onClick }, props, { children: [isElement(label) ? (React.cloneElement(label, Object.assign(Object.assign(Object.assign({}, label.props), { "aria-current": submenu ? (nestedActive ? false : active) : !submenu && active, "onClick": clickable ? handleClick : undefined, "className": cx(classes.wrapper, label.props.className) }), (collapsed &&
                submenu && {
                tabIndex: 0,
                onBlur: handleBlur,
            })), submenu && _jsx("div", { ref: combinedRef, className: classes.trigger, style: { display: collapsed ? "block" : "none" } }), renderIcon(leftIcon, classes.leftIcon), !collapsed && _jsx("div", { className: classes.text, children: label.props.children }), !collapsed && renderIcon(rightIcon || openIcon, classes.rightIcon))) : (_jsxs("div", Object.assign({ "aria-current": submenu ? (nestedActive ? false : active) : !submenu && active, className: classes.wrapper, onClick: toggleOpen }, (collapsed &&
                submenu && {
                tabIndex: 0,
                onBlur: handleBlur,
            }), { children: [submenu && _jsx("div", { ref: combinedRef, className: classes.trigger, style: { display: collapsed ? "block" : "none" } }), renderIcon(leftIcon, classes.leftIcon), !collapsed && _jsx("div", { className: classes.text, children: label }), !collapsed && renderIcon(rightIcon || openIcon, classes.rightIcon)] }))), !collapsed && submenu && React.cloneElement(submenu, Object.assign(Object.assign({}, submenu.props), { collapsed, indentLevel, open: nestedOpen || open })), collapsed && open && submenu && (_jsx(Attachment, { ref: attachmentRef, anchorRef: combinedRef, className: classes.attachment, scrollLock: false, strategy: "align-left", tabIndex: 0, onBlur: handleBlur, children: submenu &&
                    React.cloneElement(submenu, Object.assign(Object.assign({}, submenu.props), { onClose: toggleOpen, label: _jsx("li", { className: classes.label, children: isElement(label) ? label.props.children : label }), open: nestedOpen || open })) }))] })));
});
export default MenuItem;
//# sourceMappingURL=menu-item.js.map